<script setup>
import PurchaseItemsTable from '@/components/purchase/PurchaseItemsTable.vue'
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Purchase',
  },
})

const route  = useRoute()
const router = useRouter()
const { loading, errors, request } = useApiRequest()

// ─── Form ─────────────────────────────────────────────────────────────────────
const form = ref({
  purchase_order_id: route.query.po_id ? Number(route.query.po_id) : null,
  received_at:       new Date().toISOString().substring(0, 10),
  notes:             '',
})

// Shape generik dipakai PurchaseItemsTable — lihat catatan di komponennya.
// expiry_date (Addendum 10, Modul AP): opsional, cuma relevan di Receiving.
const items = ref([{ ingredient_id: null, unit_id: null, quantity: 1, total_price: 0, expiry_date: null }])

// ─── Dropdown options ─────────────────────────────────────────────────────────
const purchaseOrders = ref([])
const ingredients    = ref([])
const units          = ref([])
const optLoading     = ref(true)

onMounted(async () => {
  try {
    const [poRes, iRes, uRes] = await Promise.all([
      useApi('/api/v1/purchase-orders', { query: { per_page: 200 } }).get(),
      useApi('/api/v1/ingredients', { query: { per_page: 500 } }).get(),
      useApi('/api/v1/units', { query: { per_page: 200 } }).get(),
    ])

    const poBody = poRes?.data ?? poRes
    const iBody  = iRes?.data  ?? iRes
    const uBody  = uRes?.data  ?? uRes

    const poList = Array.isArray(poBody) ? poBody : (poBody?.data ?? [])

    purchaseOrders.value = [
      { title: '— Direct Receiving (tanpa PO) —', value: null },
      ...poList
        .filter(p => ['sent', 'partial'].includes(p.status))
        .map(p => ({ title: `${p.po_number} — ${p.supplier?.name ?? ''}`, value: p.id })),
    ]

    // unitId = satuan dasar ingredient ini sendiri — dipakai PurchaseItemsTable
    // utk filter pilihan "Satuan Pembelian" (satuan dasar + turunannya saja).
    ingredients.value = (Array.isArray(iBody) ? iBody : (iBody?.data ?? [])).map(i => ({
      title: i.name,
      value: i.id,
      unitId: i.unit?.id ?? null,
    }))

    // Satuan TIDAK di-map ke {title,value} — PurchaseItemsTable butuh field
    // aslinya (name, symbol, base_unit_id, conversion_factor) utk konversi.
    units.value = Array.isArray(uBody) ? uBody : (uBody?.data ?? [])
  } catch (err) {
    console.error('[ReceivingCreate] loadOptions error', err)
  } finally {
    optLoading.value = false
  }

  // Pre-fill items jika ada po_id dari query param
  if (form.value.purchase_order_id) {
    await prefillFromPo(form.value.purchase_order_id)
  }
})

// ─── Auto-prefill items dari PO ───────────────────────────────────────────────
const prefillFromPo = async (poId) => {
  if (!poId) return
  try {
    const response = await useApi(`/api/v1/purchase-orders/${poId}`).get()
    const body = response?.data ?? response
    const po   = body?.data ?? body

    if (po?.items?.length) {
      items.value = po.items.map(i => ({
        ingredient_id: i.ingredient_id,
        unit_id:       i.unit_id,
        quantity:      i.quantity_ordered,
        total_price:   i.total_price ?? i.subtotal,
        expiry_date:   null,
      }))
    }
  } catch (err) {
    console.error('[ReceivingCreate] prefillFromPo error', err)
  }
}

// Watch PO selection change
watch(() => form.value.purchase_order_id, async (val) => {
  if (val) {
    await prefillFromPo(val)
  } else {
    // Reset ke 1 row kosong saat PO di-clear
    items.value = [{ ingredient_id: null, unit_id: null, quantity: 1, total_price: 0, expiry_date: null }]
  }
})

// ─── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  const payload = {
    ...form.value,
    items: items.value.map(i => ({
      ingredient_id: i.ingredient_id,
      unit_id: i.unit_id,
      quantity_received: i.quantity,
      total_price: i.total_price,
      // Addendum 10, Modul AP — opsional, kosong = tidak buat ProductBatch.
      expiry_date: i.expiry_date || null,
    })),
  }

  const result = await request(() =>
    useApi('/api/v1/purchase-receivings').post({ body: payload })
  )

  if (result !== null) {
    const body = result?.data ?? result
    const id   = body?.id ?? body?.data?.id
    router.push({ name: 'purchase-receivings-id', params: { id } })
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="pt-5 px-5">Buat Purchase Receiving</VCardTitle>

        <VDivider />

        <VCardText class="pa-5">
          <VAlert
            v-if="errors._general"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ errors._general[0] }}
          </VAlert>

          <!-- Header -->
          <VRow class="mb-2">
            <VCol cols="12" md="5">
              <VAutocomplete
                v-model="form.purchase_order_id"
                :items="purchaseOrders"
                :loading="optLoading"
                label="Purchase Order (opsional)"
                :error-messages="errors.purchase_order_id"
                clearable
                no-data-text="Tidak ada PO tersedia"
              />
            </VCol>
            <VCol cols="12" md="3">
              <VTextField
                v-model="form.received_at"
                label="Tanggal Terima *"
                type="date"
                :error-messages="errors.received_at"
              />
            </VCol>
            <VCol cols="12" md="4">
              <VTextarea
                v-model="form.notes"
                label="Catatan"
                rows="2"
                auto-grow
              />
            </VCol>
          </VRow>

          <!-- Info banner -->
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4 text-body-2"
          >
            Stok bahan baku <strong>belum berubah</strong> sampai receiving dikonfirmasi.
          </VAlert>

          <!-- Items -->
          <div class="text-subtitle-2 font-weight-semibold mb-3">Item yang Diterima</div>

          <PurchaseItemsTable
            v-model="items"
            :ingredients="ingredients"
            :units="units"
            :errors="errors"
            :loading="optLoading"
            quantity-label="Qty Diterima"
            show-expiry-date
          />
        </VCardText>

        <VDivider />

        <VCardActions class="pa-5">
          <VSpacer />
          <VBtn variant="tonal" :to="{ name: 'purchase-receivings' }">Batal</VBtn>
          <VBtn color="primary" :loading="loading" @click="submit">
            Simpan (Draft)
          </VBtn>
        </VCardActions>
      </VCard>
    </VCol>
  </VRow>
</template>